import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';

import { observer } from 'mobx-react-lite';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions/useMarginPositionDetails';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';

import { MarginPositionDetailsDeletePendingOrderModal } from '../MarginPositionDetailsDeletePendingOrderModal';

type Props = {
  positionId?: string;
  onNextStep: (step: number, pushToStack?: boolean | undefined) => void;
  onClose?: () => void;
};

const MarginPositionDetailsRootStepActions: React.FC<Props> = observer(({ onNextStep, positionId, onClose }) => {
  const { isPending, isClosed, positionMarket, position, canPartialClose } = useMarginPositionDetails({ positionId });
  const { account } = useMarginAccount();
  const { isStalePriceData } = useMarginPrices();
  const { t } = useTranslation('margin');

  if (isClosed || !position) return null;

  if (isPending)
    return (
      <FlexLayout spacing={16}>
        <MarginPositionDetailsDeletePendingOrderModal positionId={positionId} onRemove={onClose}>
          <Button variant='outlined' size='lg' className='w-full' disabled={!account?.active}>
            Delete order
          </Button>
        </MarginPositionDetailsDeletePendingOrderModal>
        <Button
          variant='filled'
          disabled={!account?.active || isStalePriceData()}
          tooltip={isStalePriceData() ? t('stalePrice.tooltips.edit') : undefined}
          size='lg'
          className='w-full'
          onClick={() => onNextStep(MarginPositionDetailsStep.ManageProtectionOrders)}
        >
          Edit order
        </Button>
      </FlexLayout>
    );

  return (
    <FlexLayout spacing={16}>
      <Button
        variant='outlined'
        size='lg'
        className='w-full'
        disabled={!canPartialClose || !account?.active}
        onClick={() => onNextStep(MarginPositionDetailsStep.PartialClosePosition)}
      >
        Partial close
      </Button>
      <Button
        variant='filled'
        data-testid='margin-order-close-position'
        size='lg'
        className='w-full'
        disabled={!positionMarket?.close || !account?.active}
        onClick={() => onNextStep(MarginPositionDetailsStep.ClosePosition)}
      >
        Close position
      </Button>
    </FlexLayout>
  );
});

export { MarginPositionDetailsRootStepActions };
